class TooltipDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleTooltipClick = this.handleTooltipClick.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.init = this.init.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    
    // Initial setup
    this.init();
    
    document.addEventListener('shopify:section:load', (event) => {
      // Check if this section was reloaded
      if (event.target.contains(this)) {
        this.init();
      }
    });
  }

  init() {
    // Setup close button
    const closeBtn = this.querySelector('.js-close-tooltip-drawer');
    if (closeBtn) {
      closeBtn.removeEventListener('click', this.closeDrawer);
      closeBtn.addEventListener('click', this.closeDrawer);
    }

    // Setup overlay click to close
    const overlay = this.querySelector('.drawer__overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.closeDrawer);
      overlay.addEventListener('click', this.closeDrawer);
    }

    // Setup tooltip click handlers
    this.setupTooltipHandlers();
    
    // Setup variant change listener
    this.setupVariantChangeListener();
  }

  setupTooltipHandlers() {
    // Remove existing listeners to prevent duplicates
    document.removeEventListener('click', this.handleTooltipClick, true);
    
    // Add new listener with capture to run before other handlers
    document.addEventListener('click', this.handleTooltipClick, true);
  }

  setupVariantChangeListener() {
    // Listen for variant changes to update tooltip data
    document.addEventListener('variant:changed', this.handleVariantChange);
  }

  handleVariantChange(event) {
    // No longer need to update tooltip data dynamically
    // The tooltip content is now static and doesn't change based on variants
  }


  handleTooltipClick(event) {
    const lightFunctionHelp = event.target.closest('.js-light-function-table-help');
    if (lightFunctionHelp) {
      const row = lightFunctionHelp.closest('.product-form__option-selector');
      const drawerEl = document.querySelector('tooltip-drawer');
      const drawerSource = row?.querySelector('details.js-tooltip-trigger .option-tooltip-content');
      if (drawerEl && drawerSource && typeof drawerEl.openDrawer === 'function') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const optionName = drawerSource.getAttribute('data-option-name') || 'Selection Information';
        drawerEl.openDrawer(drawerSource.innerHTML, optionName);
        return false;
      }
      const legacyDetails = row?.querySelector('details.product-form__option-tooltip:not(.js-tooltip-trigger)');
      if (legacyDetails) {
        event.preventDefault();
        event.stopPropagation();
        legacyDetails.querySelector('summary')?.click();
        return false;
      }
    }

    // Check if the clicked element is a tooltip summary or its child
    // Only intercept clicks on elements with js-tooltip-trigger class (these should have rich_tooltip)
    const tooltipSummary = event.target.closest('details.js-tooltip-trigger summary');
    
    if (tooltipSummary) {
      const detailsElement = tooltipSummary.closest('details');
      const tooltipContent = detailsElement.querySelector('.option-tooltip-content');
      
      // Additional safety check: Verify that this tooltip has rich content
      // Elements with js-tooltip-trigger should have rich_tooltip, but double-check
      const hasRichContent = tooltipContent && tooltipContent.querySelector('.js-rich-tooltip, [data-rich-tooltip]');
      
      // If tooltip content exists (even without rich content, if it has the class, use drawer)
      if (tooltipContent) {
        // Prevent all default behavior and stop propagation immediately
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Extract the option name from the data attribute
        const optionName = tooltipContent.getAttribute('data-option-name') || 'Selection Information';
        this.openDrawer(tooltipContent.innerHTML, optionName);
        
        // Return false to ensure no other handlers are called
        return false;
      }
    }
    
    // If no tooltip trigger found, allow default behavior (for old tooltip functionality)
    return true;
  }

  openDrawer(content, optionName = 'Selection Information') {
    const drawer = this.querySelector('.js-tooltip-drawer');
    const contentContainer = this.querySelector('#tooltip-content');
    const titleElement = this.querySelector('#tooltip-heading');
    
    if (drawer && contentContainer) {
      // Update the drawer title with the option name
      if (titleElement) {
        titleElement.textContent = optionName;
      }
      
      // Insert the tooltip content
      contentContainer.innerHTML = content;

      // Lock body scroll
      this.lockBodyScroll();
      
      // Open the drawer by adding the open attribute
      drawer.setAttribute('open', '');
      
      // Don't prevent body scroll to avoid layout shift
      // The drawer overlay will handle the visual separation

      // Enhance content: prefer rich tooltip, and initialize carousels
      this.postRenderEnhancements(contentContainer, optionName);
    }
  }

  closeDrawer() {
    const drawer = this.querySelector('.js-tooltip-drawer');
    
    if (drawer) {
      drawer.removeAttribute('open');
      // Unlock body scroll
      this.unlockBodyScroll();
    }
  }

  lockBodyScroll() {
    // Store current scroll position
    this.scrollY = window.scrollY;
    
    // Apply styles to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    // Restore body styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    // Restore scroll position
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
      this.scrollY = undefined;
    }
  }
}

customElements.define('tooltip-drawer', TooltipDrawer);

// Enhancements for TooltipDrawer
TooltipDrawer.prototype.postRenderEnhancements = function(container, optionName) {
  if (!container) return;

  // 1) Prefer Rich Tooltip content if present
  // Expected markup inside container:
  //   .js-rich-tooltip (preferred)
  //   .js-plain-tooltip (fallback)
  const rich = container.querySelector('.js-rich-tooltip, [data-rich-tooltip]');
  const plain = container.querySelector('.js-plain-tooltip, [data-plain-tooltip]');
  if (rich) {
    if (plain) plain.style.display = 'none';
    rich.style.display = '';
  }

  // 2) Initialize carousels for metaobject lists automatically
  // Find all elements with data-metaobject-list attributes and initialize carousels for them
  // This works for any variant with rich_tooltip, regardless of option name

  // Generic carousel initializer for a list element where direct children are slides
  const initCarousel = (listEl) => {
    if (!listEl || listEl.dataset.carouselInitialized === 'true') return;

    listEl.dataset.carouselInitialized = 'true';

    const slides = Array.from(listEl.children).filter((c) => c.nodeType === 1);
    if (slides.length <= 1) return; // no need for carousel

    // Wrap existing list without moving its children off the element
    const wrapper = document.createElement('div');
    wrapper.className = 'tooltip-carousel';

    // Controls
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'tooltip-carousel__prev';
    prev.setAttribute('aria-label', 'Previous');
    prev.innerHTML = '&#10094;'; // «

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'tooltip-carousel__next';
    next.setAttribute('aria-label', 'Next');
    next.innerHTML = '&#10095;'; // »

    // Prepare the existing list as the track
    const track = listEl; // reuse existing element
    track.classList.add('tooltip-carousel__track');
    track.style.display = 'flex';
    track.style.overflow = 'hidden';
    track.style.scrollBehavior = 'smooth';

    // Ensure slides occupy full viewport width each
    slides.forEach((slide) => {
      slide.classList.add('tooltip-carousel__slide');
      slide.style.flex = '0 0 100%';
    });

    // Layout wrapper (insert before and move track inside)
    track.parentElement.insertBefore(wrapper, track);
    wrapper.appendChild(prev);
    wrapper.appendChild(track);
    wrapper.appendChild(next);

    let index = 0;

    const update = () => {
      // Scroll by the visible track width to ensure consistent paging
      const trackWidth = track.getBoundingClientRect().width;
      if (!trackWidth) return;
      track.scrollTo({ left: index * trackWidth, behavior: 'smooth' });
      prev.disabled = index === 0;
      next.disabled = index >= slides.length - 1;
    };

    const goPrev = () => { if (index > 0) { index -= 1; update(); } };
    const goNext = () => { if (index < slides.length - 1) { index += 1; update(); } };

    prev.addEventListener('click', goPrev);
    next.addEventListener('click', goNext);

    // Keyboard support
    wrapper.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    });

    // Resize handling to keep alignment
    const ro = new ResizeObserver(update);
    ro.observe(track);
    window.addEventListener('resize', update);
    update();
  };

  // Find all metaobject lists with data-metaobject-list attribute
  // Support multiple selector patterns for flexibility
  const metaobjectLists = container.querySelectorAll(
    '[data-metaobject-list], [data-metaobject], .metaobject-list[data-type], .metaobject-list'
  );

  // Initialize carousels for all found lists
  metaobjectLists.forEach(initCarousel);
};